import React, { useState } from 'react'
import { T } from '@transifex/react'
import { Card, Icon, Button } from '@lightspeed/design-system-react'
import { Currency } from '@vend/business-components'
import {
  IconCheckCircleSolid20,
  IconScheduleSolid20,
} from '@lightspeed/unified-react-components'
import { PaymentRequestQueryResponse } from '../../../../functions/invoicing/fetch-invoicing-payment-requests'
import {
  getPaymentRequestViewURL,
  formatDate,
} from '../../../../functions/invoicing/payment-request-resources'
import { useRetailerSettingsManager } from '../../../../ng-compat/hooks'

interface Props {
  paymentRequests: PaymentRequestQueryResponse[]
}

const PaymentRequestStatus = {
  SENT: 'sent', // sent status is treated as pending
  PAID: 'paid',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
} as const

export const PaymentRequestSummaryCard: React.FC<Props> = ({
  paymentRequests,
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const retailerSettingsManager = useRetailerSettingsManager()
  const retailer = retailerSettingsManager.get()

  // Filter to only show sent/pending and paid requests per acceptance criteria
  const visibleRequests = paymentRequests.filter(
    request =>
      request.status === PaymentRequestStatus.SENT ||
      request.status === PaymentRequestStatus.PAID
  )

  if (visibleRequests.length === 0) {
    return null
  }

  const pendingRequests = visibleRequests.filter(
    request => request.status === PaymentRequestStatus.SENT
  )

  // Convert from cents to dollars
  const totalPendingAmount = pendingRequests.reduce(
    (sum, request) => sum + request.requestedAmount / 100,
    0
  )

  const getStatusIcon = (status: string) => {
    switch (status) {
      case PaymentRequestStatus.SENT:
        return (
          <IconScheduleSolid20
            style={{ marginRight: '8px' }}
            className="vd-text-info"
          />
        )
      case PaymentRequestStatus.PAID:
        return (
          <IconCheckCircleSolid20
            style={{ marginRight: '8px' }}
            className="vd-text-success"
          />
        )
      default:
        return null
    }
  }

  const getPaidDate = (request: PaymentRequestQueryResponse): string => {
    const capturedPayment = request.payments?.find(
      payment => payment.status === 'captured'
    )

    return capturedPayment ? formatDate(capturedPayment.capturedAt) : ''
  }

  const getStatusText = (request: PaymentRequestQueryResponse) => {
    // Convert from cents to dollars
    const amount = request.requestedAmount / 100

    if (request.status === PaymentRequestStatus.SENT) {
      const sentDate = formatDate(request.createdAt)
      return (
        <T
          _str="A {amount} payment request was sent on {date}."
          _context="Payment request pending status"
          amount={<Currency value={amount} />}
          date={sentDate}
        />
      )
    }

    if (request.status === PaymentRequestStatus.PAID) {
      const paidDate = getPaidDate(request)
      return (
        <T
          _str="{amount} request paid on {date}"
          _context="Payment request paid status"
          amount={<Currency value={amount} />}
          date={paidDate}
        />
      )
    }

    return null
  }

  const handleViewClick = (request: PaymentRequestQueryResponse) => {
    const viewUrl = getPaymentRequestViewURL(request.proposalHash, retailer)
    window.open(viewUrl, '_blank', 'noopener,noreferrer')
  }

  const renderSingleRequest = () => {
    const request = visibleRequests[0]
    return (
      <Card
        className="wr-payment-request-summary-card"
        style={{ marginBottom: '16px' }}
      >
        <div style={{ padding: '16px 20px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flexShrink: 0, paddingTop: '2px' }}>
              {getStatusIcon(request.status)}
            </div>
            <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
              <div
                style={{
                  marginBottom: '12px',
                  lineHeight: '1.5',
                  fontSize: '14px',
                }}
              >
                {getStatusText(request)}
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  link
                  className="vd-link vd-link--secondary"
                  onClick={() => handleViewClick(request)}
                >
                  <T _str="View" _context="Payment request view link" />
                  <Icon icon="fa-external-link" style={{ marginLeft: '4px' }} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  const renderMultipleRequests = () => {
    return (
      <Card
        className="wr-payment-request-summary-card"
        style={{ marginBottom: '16px' }}
      >
        <div
          style={{
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            gap: '12px',
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              flex: 1,
            }}
          >
            <strong style={{ fontSize: '15px' }}>
              <T
                _str="Payment requests"
                _context="Payment requests section title"
              />
            </strong>
            {pendingRequests.length > 0 && (
              <span
                className="vd-text-info"
                style={{
                  fontSize: '12px',
                  padding: '2px 6px',
                  backgroundColor: '#e0e0e0',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  whiteSpace: 'nowrap',
                }}
              >
                <T
                  _str="PENDING {amount}"
                  _context="Payment requests pending badge"
                  amount={<Currency value={totalPendingAmount} />}
                />
              </span>
            )}
          </div>
          <Icon
            icon={isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}
            style={{ flexShrink: 0 }}
          />
        </div>
        {isExpanded && (
          <div style={{ padding: '0 20px 20px' }}>
            {visibleRequests.map((request, index) => (
              <div
                key={request.id}
                style={{
                  paddingTop: index === 0 ? '12px' : '16px',
                  paddingBottom:
                    index === visibleRequests.length - 1 ? '0' : '16px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flexShrink: 0, paddingTop: '2px' }}>
                    {getStatusIcon(request.status)}
                  </div>
                  <div style={{ flex: '1 1 auto', minWidth: '200px' }}>
                    <div
                      style={{
                        marginBottom: '12px',
                        lineHeight: '1.5',
                        fontSize: '14px',
                      }}
                    >
                      {getStatusText(request)}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '16px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <Button
                        link
                        className="vd-link vd-link--secondary"
                        onClick={() => handleViewClick(request)}
                      >
                        <T _str="View" _context="Payment request view link" />
                        <Icon
                          icon="fa-external-link"
                          style={{ marginLeft: '4px' }}
                        />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    )
  }

  return visibleRequests.length === 1
    ? renderSingleRequest()
    : renderMultipleRequests()
}
